import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { soundManager } from '../services/SoundManager';
import { ProgressionEngine, ProgressionEventType, ProgressionResult } from '../engine/ProgressionEngine';
import { DEFAULT_STATE } from '../types/progression';

interface MagicContextType {
    // State
    stars: number;
    blueTokens: number;
    redTokens: number;
    inventory: string[];

    // Actions (all go through ProgressionEngine)
    processEvent: (_type: ProgressionEventType, _payload?: Record<string, string>) => ProgressionResult;
    addStars: (_count: number) => void;
    unlockItem: (_id: string) => void;

    // Navigation
    activeRealmId: string;
    setActiveRealmId: (_id: string) => void;

    // Sound controls
    isMuted: boolean;
    toggleMute: () => void;

    // Clue/Discovery state
    pendingClueId: string | null;
    isHotspotArmed: (_hotspotId: string) => boolean;
    isOwlArmed: boolean;
    isMirrorProphecyAvailable: boolean;

    // Egg hatch state
    showEggHatch: boolean;
    setShowEggHatch: (_show: boolean) => void;

    // Loading state
    isLoading: boolean;
}

const MagicContext = createContext<MagicContextType | null>(null);

interface MagicProviderProps {
    children: ReactNode;
}

/**
 * Magic Bridge - Central state provider for Sparkle World
 * Now integrated with ProgressionEngine for persistent state and game logic.
 */
export function MagicProvider({ children }: MagicProviderProps) {
    // Initialize from engine (guaranteed non-nullable by engine fix)
    const initialState = ProgressionEngine.getState() || DEFAULT_STATE;

    const [stars, setStars] = useState<number>(initialState.totals?.goldStars || 0);
    const [blueTokens, setBlueTokens] = useState<number>(initialState.totals?.blueTokens || 0);
    const [redTokens, setRedTokens] = useState<number>(initialState.totals?.redTokens || 0);
    const [inventory, setInventory] = useState<string[]>(initialState.inventory || []);
    const [activeRealmId, setActiveRealmId] = useState<string>('home');
    const [isMuted, setIsMuted] = useState<boolean>(soundManager.isMuted());
    const [pendingClueId, setPendingClueId] = useState<string | null>(initialState.discovery?.pendingClueId || null);
    const [isOwlArmed, setIsOwlArmed] = useState<boolean>(ProgressionEngine.isOwlArmed());
    const [isMirrorProphecyAvailable, setIsMirrorProphecyAvailable] = useState<boolean>(ProgressionEngine.isMirrorProphecyAvailable());
    const [showEggHatch, setShowEggHatch] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState(false);

    // Initialize Progression Engine
    useEffect(() => {
        const initEngine = async () => {
            try {
                await ProgressionEngine.init();
                const state = ProgressionEngine.getState();

                setStars(state.totals.goldStars);
                setBlueTokens(state.totals.blueTokens);
                setRedTokens(state.totals.redTokens);
                setInventory([...state.inventory]);
                setPendingClueId(state.discovery.pendingClueId);
                setIsOwlArmed(ProgressionEngine.isOwlArmed());
                setIsMirrorProphecyAvailable(ProgressionEngine.isMirrorProphecyAvailable());

                // Trigger initial events after load
                const initialResult = ProgressionEngine.processEvent({ type: 'app_opened' });
                if (initialResult.eggHatchTriggered) {
                    setShowEggHatch(true);
                }

                ProgressionEngine.processEvent({
                    type: 'realm_changed',
                    payload: { realmId: activeRealmId }
                });
                // Opening the app can schedule a new clue before the subscription mounts.
                setPendingClueId(ProgressionEngine.getState().discovery.pendingClueId);
                setIsMirrorProphecyAvailable(ProgressionEngine.isMirrorProphecyAvailable());
            } catch (err) {
                console.error('[MagicContext] Error during engine initialization:', err);
                setLoadError(true);
            } finally {
                setIsLoading(false);
            }
        };

        initEngine();
    // One app-open per provider mount; realm navigation must not rehydrate the game.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSetActiveRealmId = useCallback((id: string) => {
        setActiveRealmId(id);
        ProgressionEngine.processEvent({
            type: 'realm_changed',
            payload: { realmId: id }
        });
    }, []);

    // Subscribe to ProgressionEngine updates
    useEffect(() => {
        if (isLoading) return;

        const unsubscribe = ProgressionEngine.subscribe((result: ProgressionResult) => {
            // Update local state from engine result
            setStars(result.newState.totals.goldStars);
            setBlueTokens(result.newState.totals.blueTokens);
            setRedTokens(result.newState.totals.redTokens);
            setInventory([...result.newState.inventory]);
            setPendingClueId(result.newState.discovery.pendingClueId);
            setIsOwlArmed(ProgressionEngine.isOwlArmed());
            setIsMirrorProphecyAvailable(ProgressionEngine.isMirrorProphecyAvailable());

            // Play sound cues from engine
            if (result.soundCues && result.soundCues.length > 0) {
                result.soundCues.forEach(cue => soundManager.play(cue));
            }

            // Trigger egg hatch if needed
            if (result.eggHatchTriggered && !showEggHatch) {
                setShowEggHatch(true);
            }
        });

        return unsubscribe;
    }, [showEggHatch, isLoading]);

    // Check owl armed state periodically
    useEffect(() => {
        const interval = setInterval(() => {
            setIsOwlArmed(ProgressionEngine.isOwlArmed());
            setIsMirrorProphecyAvailable(ProgressionEngine.isMirrorProphecyAvailable());
        }, 60000); // Every minute
        return () => clearInterval(interval);
    }, []);

    // Process any progression event through the engine
    const processEvent = useCallback((type: ProgressionEventType, payload?: Record<string, string>): ProgressionResult => {
        return ProgressionEngine.processEvent({ type, payload });
    }, []);

    // Legacy addStars - now routes through engine for debug/testing
    const addStars = useCallback((count: number) => {
        ProgressionEngine.debugAddStars(count);
    }, []);

    const unlockItem = useCallback((id: string) => {
        ProgressionEngine.debugUnlockItem(id);
        const state = ProgressionEngine.getState();
        setInventory([...state.inventory]);
    }, []);

    const toggleMute = useCallback(() => {
        const newMuted = soundManager.toggleMute();
        setIsMuted(newMuted);
    }, []);

    const isHotspotArmed = useCallback((hotspotId: string): boolean => {
        return ProgressionEngine.isHotspotArmed(hotspotId);
    }, []);

    const value: MagicContextType = {
        stars,
        blueTokens,
        redTokens,
        inventory,
        processEvent,
        addStars,
        unlockItem,
        activeRealmId,
        setActiveRealmId: handleSetActiveRealmId,
        isMuted,
        toggleMute,
        pendingClueId,
        isHotspotArmed,
        isOwlArmed,
        isMirrorProphecyAvailable,
        showEggHatch,
        setShowEggHatch,
        isLoading,
    };

    if (loadError) return <div className="storage-recovery"><h1>Your magic is taking a moment.</h1><p>This browser could not open your saved progress. Your existing data has not been replaced. Check that website storage is available, then try again.</p><button className="action-primary" onClick={() => window.location.reload()}>Try again</button></div>;
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a] text-sparkle-gold">
                <div className="animate-pulse text-2xl font-medieval">Gathering Magic...</div>
            </div>
        );
    }

    return (
        <MagicContext.Provider value={value}>
            {children}
        </MagicContext.Provider>
    );
}

/**
 * Hook to access Magic Bridge state and actions
 */
export function useMagic(): MagicContextType {
    const context = useContext(MagicContext);
    if (!context) {
        throw new Error('useMagic must be used within a MagicProvider');
    }
    return context;
}
